/**
 * ATT-3464 — telemetry for the central auth flow (design.md §D8).
 *
 * The staged rollout removes the per-tenant redirect URIs only once the central
 * path is verified working, so its success/failure rate has to be observable.
 *
 * Two halves, because neither alone is sufficient:
 *   - CLIENT events mark the two points the browser actually reaches (flow
 *     started, user landed back on their tenant);
 *   - a SERVER record marks a refusal, which the client can never observe — the
 *     callback refuses before any page renders.
 *
 * PII: payloads carry a tenant HOST and an outcome code, nothing else.
 * `openspec/nfr.md` forbids logging emails, names, or raw tokens, and this is a
 * sign-in path — the shape below is deliberately closed rather than a
 * pass-through of whatever the caller supplies.
 */

export type CentralAuthOutcome = "started" | "completed" | "refused";

export type CentralAuthEvent = {
  outcome: CentralAuthOutcome;
  /** The request host, or the origin tenant host. Never a user identifier. */
  host?: string;
  /** A stable machine code (e.g. `origin_cookie_expired`), never free text. */
  reason?: string;
};

/**
 * Record a server-side central-auth outcome.
 *
 * Uses `console.error` with the same `[...]`-prefixed convention as the
 * NextAuth logger already in `pages/api/auth/[...nextauth].ts`, so central-auth
 * refusals are greppable next to the other auth failure modes in the platform
 * logs. `no-console` is disabled here for the same reason it is there.
 */
export function recordCentralAuthEvent(event: CentralAuthEvent): void {
  // Destructure explicitly: anything else a caller passes is dropped rather
  // than serialized, so an accidental email/token can never reach the log.
  const { outcome, host, reason } = event;
  // eslint-disable-next-line no-console
  console.error(
    `[central-auth][${outcome}] host=${host || "<unknown-host>"} reason=${reason || "none"}`,
  );
}

export type CentralAuthClientEventName =
  | "auth_google_central_start"
  | "auth_google_central_return";

type GtagWindow = Window & {
  gtag?: (command: "event", name: string, params: Record<string, string>) => void;
};

/**
 * Emit a client-side central-auth event via the GTM `gtag` already loaded in
 * `pages/_app.tsx`.
 *
 * A no-op when `gtag` is absent (ad blocker, GTM not configured, SSR) — losing
 * a metric must never be able to break a sign-in.
 */
export function trackCentralAuthClientEvent(
  name: CentralAuthClientEventName,
  params: { host: string },
): void {
  if (typeof window === "undefined") return;
  const { gtag } = window as GtagWindow;
  if (typeof gtag !== "function") return;
  try {
    gtag("event", name, { tenant_host: params.host });
  } catch {
    // Analytics must never surface an error into the auth flow.
  }
}
